/**
 * CBB Headshot Service
 *
 * Provides programmatic access to college basketball player headshots via ESPN CDN.
 * URL Pattern: https://a.espncdn.com/combiner/i?img=/i/headshots/mens-college-basketball/players/full/{espnId}.png
 *
 * ESPN CDN is CORS-enabled (access-control-allow-origin: *) so images load directly in browser.
 * No API key required. 100% D1 coverage.
 *
 * @example
 * import { getCBBHeadshotUrl, fetchTeamHeadshots } from './cbbHeadshotService';
 * const url = getCBBHeadshotUrl(5105600); // Cooper Flagg
 */

// =============================================================================
// ESPN CDN URL PATTERNS
// =============================================================================

/**
 * ESPN CDN bases
 */
const ESPN_CDN_BASE = 'https://a.espncdn.com';
const ESPN_HEADSHOT_PATH_MBB = '/i/headshots/mens-college-basketball/players/full';
const ESPN_HEADSHOT_PATH_WBB = '/i/headshots/womens-college-basketball/players/full';
const ESPN_API_BASE = 'https://site.api.espn.com/apis/site/v2/sports/basketball';

/**
 * Available headshot size presets
 */
export const CBB_HEADSHOT_SIZES = {
  SMALL: { width: 96, height: 96 },
  MEDIUM: { width: 213, height: 213 },
  LARGE: { width: 350, height: 350 },
};

// =============================================================================
// IN-MEMORY CACHE
// =============================================================================

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const teamHeadshotCache = new Map();
const searchCache = new Map();

// =============================================================================
// HEADSHOT URL GENERATORS
// =============================================================================

/**
 * Generate ESPN CDN headshot URL for a college basketball player.
 *
 * Uses the ESPN combiner for on-the-fly resizing.
 * Pattern: https://a.espncdn.com/combiner/i?img=/i/headshots/mens-college-basketball/players/full/{espnId}.png&w={width}&h={height}&cb=1
 *
 * @param {number|string} espnId - The ESPN player ID
 * @param {Object} options - Configuration options
 * @param {string} options.size - Size preset from CBB_HEADSHOT_SIZES (default: 'MEDIUM')
 * @param {string} options.gender - 'mens' or 'womens' (default: 'mens')
 * @param {number} options.customWidth - Override width
 * @param {number} options.customHeight - Override height
 * @returns {string} - Full URL to the player's headshot
 *
 * @example
 * getCBBHeadshotUrl(5105600) // Cooper Flagg - default medium
 * getCBBHeadshotUrl(5105600, { size: 'LARGE' })
 * getCBBHeadshotUrl(5105600, { gender: 'womens' })
 */
export function getCBBHeadshotUrl(espnId, options = {}) {
  if (!espnId) {
    console.warn('[CBBHeadshot] No espnId provided');
    return getCBBPlaceholderUrl(options);
  }

  // Normalize espnId to integer to prevent URL injection
  const safeId = parseInt(espnId, 10);
  if (isNaN(safeId)) {
    console.warn('[CBBHeadshot] Invalid espnId:', espnId);
    return getCBBPlaceholderUrl(options);
  }

  const {
    size = 'MEDIUM',
    gender = 'mens',
    customWidth = null,
    customHeight = null,
  } = options;

  const sizeConfig = CBB_HEADSHOT_SIZES[size] || CBB_HEADSHOT_SIZES.MEDIUM;
  const width = customWidth || sizeConfig.width;
  const height = customHeight || sizeConfig.height;

  const headPath = gender === 'womens' ? ESPN_HEADSHOT_PATH_WBB : ESPN_HEADSHOT_PATH_MBB;

  // Use ESPN combiner for resized images
  return `${ESPN_CDN_BASE}/combiner/i?img=${headPath}/${safeId}.png&w=${width}&h=${height}&cb=1`;
}

/**
 * Generate a direct (full-res) ESPN CDN headshot URL.
 * Use this when you want the full resolution PNG without combiner resizing.
 *
 * @param {number|string} espnId - The ESPN player ID
 * @param {string} gender - 'mens' or 'womens' (default: 'mens')
 * @returns {string} - Full URL to the player's full-res headshot
 */
export function getCBBHeadshotUrlDirect(espnId, gender = 'mens') {
  if (!espnId) return getCBBPlaceholderUrl();

  const headPath = gender === 'womens' ? ESPN_HEADSHOT_PATH_WBB : ESPN_HEADSHOT_PATH_MBB;
  return `${ESPN_CDN_BASE}${headPath}/${espnId}.png`;
}

/**
 * Generate a placeholder URL when no headshot is available.
 * Returns a generic silhouette from ESPN CDN.
 *
 * @param {Object} options
 * @param {string} options.size - Size preset (default: 'MEDIUM')
 * @returns {string}
 */
export function getCBBPlaceholderUrl(options = {}) {
  const { size = 'MEDIUM' } = options;
  const sizeConfig = CBB_HEADSHOT_SIZES[size] || CBB_HEADSHOT_SIZES.MEDIUM;
  // ESPN generic headshot placeholder
  return `${ESPN_CDN_BASE}/combiner/i?img=${ESPN_HEADSHOT_PATH_MBB}/0.png&w=${sizeConfig.width}&h=${sizeConfig.height}&cb=1`;
}

/**
 * Generate ESPN team logo URL.
 *
 * @param {number|string} espnTeamId - ESPN team ID
 * @param {number} size - Logo size in pixels (default: 500)
 * @returns {string}
 */
export function getCBBTeamLogoUrl(espnTeamId, size = 500) {
  if (!espnTeamId) return null;
  return `${ESPN_CDN_BASE}/i/teamlogos/ncaa/${size}/${espnTeamId}.png`;
}

// =============================================================================
// ESPN API FETCHERS
// =============================================================================

/**
 * Fetch all headshots for a team from ESPN roster API.
 * Returns a name-to-headshot mapping for easy lookup.
 * Caches results in-memory for 24 hours.
 *
 * @param {number|string} espnTeamId - ESPN team ID
 * @param {string} gender - 'mens' or 'womens' (default: 'mens')
 * @returns {Promise<Object>} - Map of lowercased player name → { espnId, headshotUrl, smallUrl, fullName, position, jersey }
 *
 * @example
 * const headshots = await fetchTeamHeadshots(150); // Duke
 * headshots['cooper flagg'] // { espnId: 5105600, headshotUrl: '...', ... }
 */
export async function fetchTeamHeadshots(espnTeamId, gender = 'mens') {
  if (!espnTeamId) {
    console.warn('[CBBHeadshot] No espnTeamId provided');
    return {};
  }

  const cacheKey = `${gender}-${espnTeamId}`;
  const cached = teamHeadshotCache.get(cacheKey);
  if (cached && Date.now() < cached.expiry) {
    return cached.data;
  }

  try {
    // Route through backend proxy to avoid CSP/CORS issues with ESPN API
    const genderParam = gender !== 'mens' ? `?gender=${gender}` : '';
    const response = await fetch(`/api/basketball/headshots/${espnTeamId}${genderParam}`);

    if (!response.ok) {
      console.error(`[CBBHeadshot] Backend headshot proxy returned ${response.status} for team ${espnTeamId}`);
      return {};
    }

    const result = await response.json();
    const players = result.data?.players || result.players || {};

    // Cache for 24 hours
    teamHeadshotCache.set(cacheKey, { data: players, expiry: Date.now() + CACHE_TTL_MS });
    return players;
  } catch (err) {
    console.error(`[CBBHeadshot] Failed to fetch team roster:`, err.message);
    return {};
  }
}

/**
 * Search for a player headshot by name using ESPN search API.
 * Useful as a fallback when espnId is not known.
 *
 * @param {string} playerName - Player name to search
 * @param {string} gender - 'mens' or 'womens' (default: 'mens')
 * @returns {Promise<Object|null>} - { espnId, fullName, headshotUrl, smallUrl, team } or null
 *
 * @example
 * const result = await searchPlayerHeadshot('Cooper Flagg');
 * // { espnId: 5105600, fullName: 'Cooper Flagg', headshotUrl: '...', ... }
 */
export async function searchPlayerHeadshot(playerName, gender = 'mens') {
  if (!playerName) return null;

  const cacheKey = `search-${gender}-${playerName.toLowerCase()}`;
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() < cached.expiry) {
    return cached.data;
  }

  const league = gender === 'womens'
    ? 'womens-college-basketball'
    : 'mens-college-basketball';

  try {
    const url = `https://site.api.espn.com/apis/search/v2?query=${encodeURIComponent(playerName)}&sport=basketball&league=${league}&type=player&limit=5`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error(`[CBBHeadshot] ESPN search API returned ${response.status}`);
      return null;
    }

    const data = await response.json();
    const items = data.items || data.results || [];

    if (items.length === 0) return null;

    // Take the first match
    const match = items[0];
    const espnId = match.id || match.$ref?.match(/athletes\/(\d+)/)?.[1];

    if (!espnId) return null;

    const result = {
      espnId,
      fullName: match.displayName || match.name || playerName,
      team: match.team?.displayName || '',
      headshotUrl: getCBBHeadshotUrl(espnId, { size: 'MEDIUM', gender }),
      smallUrl: getCBBHeadshotUrl(espnId, { size: 'SMALL', gender }),
      largeUrl: getCBBHeadshotUrl(espnId, { size: 'LARGE', gender }),
    };

    // Cache for 24 hours
    searchCache.set(cacheKey, { data: result, expiry: Date.now() + CACHE_TTL_MS });
    return result;
  } catch (err) {
    console.error(`[CBBHeadshot] Search failed for "${playerName}":`, err.message);
    return null;
  }
}

/**
 * Clear all caches (useful for testing or forced refresh).
 */
export function clearCBBHeadshotCache() {
  teamHeadshotCache.clear();
  searchCache.clear();
}

// =============================================================================
// PRELOAD UTILITIES
// =============================================================================

/**
 * Preload headshot images for better UX.
 *
 * @param {Array<number|string>} espnIds - Array of ESPN IDs to preload
 * @param {Object} options - Headshot options
 */
export function preloadCBBHeadshots(espnIds, options = {}) {
  espnIds.forEach(espnId => {
    const img = new Image();
    img.src = getCBBHeadshotUrl(espnId, options);
  });
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  getCBBHeadshotUrl,
  getCBBHeadshotUrlDirect,
  getCBBPlaceholderUrl,
  getCBBTeamLogoUrl,
  fetchTeamHeadshots,
  searchPlayerHeadshot,
  clearCBBHeadshotCache,
  preloadCBBHeadshots,
  CBB_HEADSHOT_SIZES,
};
